import { gql, useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type { Board, NoteVisibility } from '@sbrc/gql/apollo-codegen';

const LOAD_BOARD_NOTES = gql`
  query LoadBoardNotes($boardSerialNumber: String!, $limit: Int, $offset: Int) {
    board(boardSerialNumber: $boardSerialNumber) {
      id
      notes(limit: $limit, offset: $offset) {
        items {
          id
          created
          content
          visibility
          author {
            id
            name
          }
        }
      }
    }
  }
`;

const CREATE_BOARD_NOTE = gql`
  mutation CreateBoardNote(
    $boardId: String!
    $content: String!
    $visibility: NoteVisibility!
  ) {
    createNote(
      category: "board"
      content: $content
      referenceId: $boardId
      visibility: $visibility
    ) {
      id
    }
  }
`;

const DELETE_BOARD_NOTE = gql`
  mutation DeleteBoardNote($id: ID!) {
    deleteNote(id: $id) {
      ignore
    }
  }
`;

interface LoadBoardNotesQuery {
  board?: Pick<Board, 'id' | 'notes'> | null;
}

interface LoadBoardNotesQueryVariables {
  boardSerialNumber: string;
  limit?: number;
  offset?: number;
}

interface CreateBoardNoteMutation {
  createNote: { id: string };
}

interface CreateBoardNoteMutationVariables {
  boardId: string;
  content: string;
  visibility: NoteVisibility;
}

interface DeleteBoardNoteMutation {
  deleteNote: { ignore: boolean };
}

interface DeleteBoardNoteMutationVariables {
  id: string;
}

export const useBoardNotes = (
  boardSerialNumber: string,
  limit: number = 20,
  offset: number = 0,
) => {
  const { data, loading, error, refetch } = useQuery<
    LoadBoardNotesQuery,
    LoadBoardNotesQueryVariables
  >(LOAD_BOARD_NOTES, {
    variables: { boardSerialNumber, limit, offset },
    skip: !boardSerialNumber,
  });

  const [createNoteMutation, { loading: createLoading, error: createError }] =
    useMutation<CreateBoardNoteMutation, CreateBoardNoteMutationVariables>(
      CREATE_BOARD_NOTE,
    );

  const [deleteNoteMutation, { loading: deleteLoading, error: deleteError }] =
    useMutation<DeleteBoardNoteMutation, DeleteBoardNoteMutationVariables>(
      DELETE_BOARD_NOTE,
    );

  const notes = data?.board?.notes.items ?? [];
  const boardId = data?.board?.id;

  const createBoardNote = useCallback(
    async (args: { content: string; visibility: NoteVisibility }) => {
      if (!boardId) return;

      await createNoteMutation({
        variables: {
          boardId,
          ...args,
        },
      });

      await refetch();
    },
    [createNoteMutation, boardId, refetch],
  );

  const deleteBoardNote = useCallback(
    async (noteId: string) => {
      await deleteNoteMutation({
        variables: {
          id: noteId,
        },
      });

      await refetch();
    },
    [deleteNoteMutation, refetch],
  );

  return {
    notes,
    loading,
    error,
    createBoardNote,
    deleteBoardNote,
    mutationLoading: createLoading || deleteLoading,
    mutationError: createError || deleteError,
    boardId,
  };
};
